import { apiFetch } from "./client";
import type {
  ChannelBody,
  HandoffOut,
  LoginResult,
  MfaConfirmBody,
  RegisterOut,
  RespondentAccountOut,
  RespondentLoginBody,
  RespondentRegisterBody,
  VerifyConfirmBody,
  VerifyConfirmOut,
  VerifyStartBody,
  VerifyStartOut,
  VerifyStatusOut,
} from "./types";

export const authApi = {
  /** Register — issues NO session (ADR-0043). Returns the account + a
   *  short-lived `verify_ticket` to drive the verify flow. */
  register: (body: RespondentRegisterBody) =>
    apiFetch<RegisterOut>("/r/auth/register", { method: "POST", body: JSON.stringify(body) }),

  /** Begin verification over the chosen channel (email code/link, or a
   *  Telegram deeplink). Re-callable to resend. */
  verifyStart: (body: VerifyStartBody) =>
    apiFetch<VerifyStartOut>("/r/auth/verify/start", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  /** Confirm a typed verification code → sets the gate. No session (re-login). */
  verifyConfirm: (body: VerifyConfirmBody) =>
    apiFetch<VerifyConfirmOut>("/r/auth/verify/confirm", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  /** Poll verification status (waits on a Telegram bind / email-link tap). */
  verifyStatus: (verifyTicket: string) =>
    apiFetch<VerifyStatusOut>(
      `/r/auth/verify/status?verify_ticket=${encodeURIComponent(verifyTicket)}`,
      { skipRefresh: true },
    ),

  /** Log in. May return `mfa_required` (no session yet) — redeem via loginMfa. */
  login: (body: RespondentLoginBody) =>
    apiFetch<LoginResult>("/r/auth/login", { method: "POST", body: JSON.stringify(body) }),

  /** Redeem the second factor → sets the session (and optionally trusts the
   *  device for 30 days). */
  loginMfa: (body: MfaConfirmBody) =>
    apiFetch<RespondentAccountOut>("/r/auth/login/mfa", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  logout: () => apiFetch<void>("/r/auth/logout", { method: "POST" }),

  refresh: () => apiFetch<RespondentAccountOut>("/r/auth/refresh", { method: "POST" }),

  me: () => apiFetch<RespondentAccountOut>("/r/auth/me"),

  /** Poll variant of `me` for the not-yet-authenticated mfa page: 401 is the
   *  expected steady state there, so skip the refresh recovery (each poll
   *  would otherwise log a 401 me + 401 refresh pair on the API). */
  mePoll: () => apiFetch<RespondentAccountOut>("/r/auth/me", { skipRefresh: true }),

  /** Change the notification/verification channel (clears verification —
   *  re-verify required). */
  setChannel: (body: ChannelBody) =>
    apiFetch<RespondentAccountOut>("/r/auth/me/channel", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  /** Re-issue a verification challenge for the logged-in respondent. */
  reverify: () => apiFetch<VerifyStartOut>("/r/auth/me/reverify", { method: "POST" }),

  /** Mint a single-use cross-host handoff token (ADR-0055) — redeemed by
   *  `GET /r/auth/handoff?ct=...` on the twin-chat host to land authenticated. */
  handoff: () => apiFetch<HandoffOut>("/r/auth/handoff", { method: "POST" }),

  /** Phase 5 — end the admin's read-only impersonation session: clears the
   *  `dca_imp` cookie server-side and writes the `impersonation.ended` audit
   *  row. Skips the 401→refresh recovery (there's no respondent session to
   *  refresh). */
  impersonationExit: () =>
    apiFetch<void>("/r/impersonate/exit", { method: "POST", skipRefresh: true }),
};
